import React, { useState, useEffect } from 'react';
import {
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Button, TablePagination,
  TextField, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle
} from '@mui/material';

import AddEmpresa from './AddEmpresa';
import { useAccesos } from '../contexts/AccesosContext';

function GestionEmpresas() {
  const { tienePermiso } = useAccesos();
  const server = process.env.REACT_APP_API_ROUTE;
  const key = process.env.REACT_APP_API_KEY;
  const [empresas, setEmpresas] = useState([]);
  const [editEmpresa, setEditEmpresa] = useState(null);  // Empresa en edición (para editar)
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openDialog, setOpenDialog] = useState(false);
  const [empresaToDelete, setEmpresaToDelete] = useState(null);

  useEffect(() => {
    fetchEmpresas();
  }, []);

  // 1. Cargar la lista de empresas
  const fetchEmpresas = async () => {
    try {
      const response = await fetch(`${server}/MgEmpresas`);
      const data = await response.json();
      // Si tu API retorna data.$values:
      setEmpresas(data.$values || data);
    } catch (error) {
      console.error('Error al cargar las empresas:', error);
      setEmpresas([]);
    }
  };

  // 2. Manejo al crear/editar: recarga la lista y limpia la empresa en edición
  const handleNewEmpresa = () => {
    fetchEmpresas();
    setEditEmpresa(null);
  };

  // 3. Búsqueda en tabla
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  // 4. Paginación
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // 5. Abrir diálogo de confirmación para eliminar
  const handleOpenDialog = (empresa) => {
    setEmpresaToDelete(empresa);
    setOpenDialog(true);
  };
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEmpresaToDelete(null);
  };

  // 6. Eliminar
  const handleDeleteEmpresa = async () => {
    if (empresaToDelete) {
      try {
        // Ajusta el campo ID real si no es 'idEmpresa'
        const response = await fetch(`${server}/MgEmpresas/${empresaToDelete.idEmpresa}`, {
          method: 'DELETE'
        });
        if (response.ok) {
          handleCloseDialog();
          fetchEmpresas();    // Recarga la lista
          alert('Empresa eliminada con éxito');
        } else {
          alert('Error al eliminar la empresa');
        }
      } catch (error) {
        console.error('Error al eliminar la empresa:', error);
        alert('Error al eliminar la empresa');
      }
    }
  };

  // Filtrado rápido (ejemplo por nombre_completo y eslogan)
  const filteredEmpresas = empresas.filter((emp) =>
  (emp.nombre_completo?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    emp.eslogan?.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <>
      <div className="right_col" role="main">
        <div className="x_title"></div>
        <div className="x_content">
          <h2>Gestión de Empresas</h2>
          <br />

          {/* Componente hijo para crear/editar */}
          <AddEmpresa
            handleNewEmpresa={handleNewEmpresa}
            editEmpresa={editEmpresa}
          />

          <div className="ln_solid" />
          <h2>Empresas Existentes</h2>

          {/* <div className="row"> */}
          {/* <div className="col-md-9 col-sm-4 offset-md-2"> */}
          {/* Campo de búsqueda */}
          <TextField
            label="Buscar Empresa"
            variant="outlined"
            fullWidth
            value={searchQuery}
            onChange={handleSearchChange}
            style={{ marginBottom: 20 }}
          />

          {/* Contenedor para scroll horizontal en la tabla */}
          <div style={{ width: '100%', overflowX: 'auto' }}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Acciones</TableCell>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Eslogan</TableCell>
                    <TableCell>Correo</TableCell>
                    <TableCell>Clave</TableCell>
                    <TableCell>Servidor Correo</TableCell>
                    <TableCell>Pagina Web</TableCell>
                    <TableCell>Imagen Fondo</TableCell>
                    <TableCell>RTN</TableCell>
                    <TableCell>Doctor Firmante</TableCell>
                    <TableCell>Imagen Firma</TableCell>
                    <TableCell>Imagen Sello</TableCell>
                    <TableCell>Imagen Reportes</TableCell>
                    <TableCell>Representante</TableCell>
                    <TableCell>Tel. Rep</TableCell>
                    <TableCell>Correo Rep</TableCell>
                    <TableCell>Cargo Rep</TableCell>
                    <TableCell>Puerto Correo</TableCell>
                    <TableCell>Requiere Auth</TableCell>
                    <TableCell>Requiere SSL</TableCell>
                    <TableCell>Abreviado</TableCell>
                    <TableCell>Título Doctor</TableCell>
                    <TableCell>% Tercera Edad</TableCell>
                    <TableCell>Tel. Empresa</TableCell>
                    <TableCell>Puntos Activos</TableCell>
                    <TableCell>% Cuarta Edad</TableCell>
                    <TableCell>Certificaciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredEmpresas
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((empresa) => (
                      <TableRow key={empresa.idEmpresa}>
                        <TableCell>
                          <Button
                            color="primary"
                            onClick={() => setEditEmpresa(empresa)}
                            disabled={!tienePermiso('Editar Empresa')}
                          >
                            Editar
                          </Button>
                          <Button
                            color="secondary"
                            onClick={() => handleOpenDialog(empresa)}
                            disabled={!tienePermiso('Eliminar Empresa')}
                          >
                            Eliminar
                          </Button>
                        </TableCell>
                        <TableCell>{empresa.nombreCompleto}</TableCell>
                        <TableCell>{empresa.eslogan}</TableCell>
                        <TableCell>{empresa.correoElectronico}</TableCell>
                        <TableCell>{empresa.clave}</TableCell>
                        <TableCell>{empresa.servidorCorreo}</TableCell>
                        <TableCell>{empresa.paginaWeb}</TableCell>
                        <TableCell>{empresa.imagenFondo}</TableCell>
                        <TableCell>{empresa.rtn}</TableCell>
                        <TableCell>{empresa.doctorFirmante}</TableCell>
                        <TableCell>{empresa.imagenFirma}</TableCell>
                        <TableCell>{empresa.imagenSello}</TableCell>
                        <TableCell>{empresa.imagenReportes}</TableCell>
                        <TableCell>{empresa.nombreRepresentante}</TableCell>
                        <TableCell>{empresa.telefonoRepresentante}</TableCell>
                        <TableCell>{empresa.correoRepresentante}</TableCell>
                        <TableCell>{empresa.cargoRepresentante}</TableCell>
                        <TableCell>{empresa.servidorCorreoPuerto}</TableCell>
                        <TableCell>
                          {empresa.requiereAutenticacion ? 'Sí' : 'No'}
                        </TableCell>
                        <TableCell>
                          {empresa.requiereSsl ? 'Sí' : 'No'}
                        </TableCell>
                        <TableCell>{empresa.nombreEmpresaAbreviado}</TableCell>
                        <TableCell>{empresa.tituloDoctor}</TableCell>
                        <TableCell>{empresa.porcentajeDescuentoTerceraEdad}</TableCell>
                        <TableCell>{empresa.telefonosDeLaEmpresa}</TableCell>
                        <TableCell>
                          {empresa.puntosActivos ? 'Sí' : 'No'}
                        </TableCell>
                        <TableCell>{empresa.porcentajeDescuentoCuartaEdad}</TableCell>
                        <TableCell>{empresa.cantidad_certificaciones}</TableCell>
                      </TableRow>
                    ))
                  }
                </TableBody>
              </Table>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredEmpresas.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableContainer>
          </div>

          {/* Diálogo para confirmar eliminación */}
          <Dialog
            open={openDialog}
            onClose={handleCloseDialog}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">{"Confirmar eliminación"}</DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                ¿Estás seguro de que deseas eliminar esta empresa?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog} color="primary">
                Cancelar
              </Button>
              <Button onClick={handleDeleteEmpresa} color="primary" autoFocus>
                Eliminar
              </Button>
            </DialogActions>
          </Dialog>

          {/* </div> */}
          {/* </div> */}
        </div>
      </div>
    </>
  );
}

export default GestionEmpresas;
